import { employeesBulkAdd } from 'api';
import { deleteJSON, fetchJSON, postFormData, postJSON } from 'api/fetch';

import { AnonCompany } from 'features/productGrowth/PostSignup/GustoIntegration/types';
import {
  BulkAddTeamMember,
  DashboardBannerData,
} from 'features/productGrowth/types';
import { apiCallWithRetry } from 'features/productGrowth/utils';

import { resolveError } from 'util/error';

export const postPayrollFirstPayDate = (startDate: string) =>
  postJSON('/payroll/post_signup/first_payday', {
    start_date: startDate,
  }).catch(resolveError);

export const postUploadDocument = (document: File) =>
  postFormData('/payroll/post_signup/upload_document', {
    document,
  });

export const validateMarketingDiscountCoupon = ({ code }: { code: string }) =>
  postJSON(`/growth/marketing_discount_coupons/validate?code=${code}`);

export const getMarketingDiscountCoupon = () =>
  fetchJSON('/growth/marketing_discount_coupons/attached_coupon');

export const getTranslatedMarketingDiscountCoupon = ({
  code,
  planName,
}: {
  [key: string]: string;
}) =>
  fetchJSON(
    `/growth/marketing_discount_coupons/translate?code=${code}&plan_name=${planName}`
  );

export const unattachMarketingDiscountCoupon = () =>
  deleteJSON('/growth/marketing_discount_coupons');

export const getLaunchWeekData = (): Promise<DashboardBannerData> =>
  fetchJSON('/launch_week_data')
    .then(data => ({
      bannerType: data.banner_type,
      isDismissable: data.is_dismissable,
    }))
    .catch(resolveError);

export const postAnonCreateExtractionProcessModal = ({
  payrollSoftwareName,
}: {
  payrollSoftwareName: string;
}): Promise<{
  url: string;
  extraction_process_id: string;
  created: string;
  expires_at: string;
}> =>
  postJSON('/onboarding/anon/modal', {
    payroll_software_name: payrollSoftwareName,
  });

export const fetchAnonExtractionProcessModal = ({
  payrollSoftwareName,
}: {
  payrollSoftwareName: string;
}): Promise<{
  url: string;
  extraction_process_id: string;
  created: string;
  expires_at: string;
}> =>
  fetchJSON('/onboarding/anon/modal', {
    payroll_software_name: payrollSoftwareName,
  });

export const postAnonAuthCallback = () =>
  postJSON('/onboarding/anon/auth_callback').catch(resolveError);

export const getAnonCompanyData = async ({
  companyUuid,
  extractionProcessId,
  step,
}: {
  companyUuid: string;
  extractionProcessId: string;
  step: 'business_details' | 'team_members';
}): Promise<AnonCompany> =>
  apiCallWithRetry<AnonCompany>(
    async () => {
      const url = `/onboarding/anon/company`;

      let processId = extractionProcessId;

      if (!processId) {
        processId = await fetchJSON(
          '/onboarding/anon/extraction_process_id'
        ).then(data => data.extraction_process_id);
      }

      // eslint-disable-next-line max-len
      const queryParams = `?company_uuid=${companyUuid}&extraction_process_id=${processId}`;

      return fetchJSON(`${url}${queryParams}`).then(data => {
        // Treat responses that still have all critical files as null as incomplete

        const valid =
          data[
            step === 'business_details'
              ? 'business_details_valid'
              : 'team_members_valid'
          ];

        if (!valid) {
          // Force retry by throwing so apiCallWithRetry retries until limit
          throw new Error('Extraction in progress – data incomplete');
        }

        return data;
      });
    },
    10, // ten retries
    6000 // six seconds
  ).catch(resolveError);

export const postAnonTeamMembers = async ({
  users,
  invite,
  eventCategory,
  productArea,
  existingUsersFlag = false,
  sendOnboardingPacket = false,
}: {
  users: Required<BulkAddTeamMember>[];
  invite: boolean;
  eventCategory?: string;
  productArea?: string;
  existingUsersFlag?: boolean;
  sendOnboardingPacket?: boolean;
}) =>
  postJSON(employeesBulkAdd(), {
    anon_integration: true,
    invite,
    users,
    existing_users: existingUsersFlag,
    send_onboarding_packet: sendOnboardingPacket,
    product_area: productArea,
    event_category: eventCategory,
  });
